import i18n from '@/i18n'

const ERR = 'pages.projectDetail.audioRecorder.errors'

type DisplayMediaStreamOptionsWithSystemAudio = DisplayMediaStreamOptions & {
	systemAudio?: 'include' | 'exclude'
	suppressLocalAudioPlayback?: boolean
}

export type MeetingAudioCaptureTier = 'unsupported' | 'tabOnly' | 'desktopApp'

export interface MeetingAudioCaptureSupport {
	tier: MeetingAudioCaptureTier
	supportsDesktopTeamsApp: boolean
}

function hasDisplayMediaApi(): boolean {
	return (
		typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
	)
}

function isChromiumDesktopBrowser(): boolean {
	if (typeof navigator === 'undefined') {
		return false
	}
	const ua = navigator.userAgent
	if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) {
		return false
	}
	return /Edg\//.test(ua) || (/Chrome\//.test(ua) && !/OPR\//.test(ua))
}

function isLikelyWindows(): boolean {
	if (typeof navigator === 'undefined') {
		return false
	}
	return /Win/i.test(navigator.userAgent) || /Win/i.test(navigator.platform ?? '')
}

export function getMeetingAudioCaptureSupport(): MeetingAudioCaptureSupport {
	if (!hasDisplayMediaApi()) {
		return {
			tier: 'unsupported',
			supportsDesktopTeamsApp: false,
		}
	}

	const likelyWindows = isLikelyWindows()
	if (isChromiumDesktopBrowser()) {
		return {
			tier: likelyWindows ? 'desktopApp' : 'tabOnly',
			supportsDesktopTeamsApp: likelyWindows,
		}
	}

	return {
		tier: 'tabOnly',
		supportsDesktopTeamsApp: false,
	}
}

export function microphoneAudioConstraints(
	selectedDeviceId: string | null,
	options: { disableBuiltInProcessing: boolean }
): MediaTrackConstraints | boolean {
	const processing = {
		echoCancellation: false,
		noiseSuppression: false,
		autoGainControl: false,
	} satisfies Pick<
		MediaTrackConstraints,
		'echoCancellation' | 'noiseSuppression' | 'autoGainControl'
	>

	if (selectedDeviceId === null) {
		return options.disableBuiltInProcessing ? { ...processing } : true
	}

	return {
		deviceId: { exact: selectedDeviceId },
		...(options.disableBuiltInProcessing ? processing : {}),
	}
}

export function mapGetUserMediaFailure(e: unknown): { message: string; reportToSentry: boolean } {
	const name =
		e && typeof e === 'object' && 'name' in e && typeof (e as DOMException).name === 'string'
			? (e as DOMException).name
			: ''
	switch (name) {
		case 'NotAllowedError':
		case 'PermissionDenied':
			return { message: i18n.t(`${ERR}.permissionDenied`), reportToSentry: false }
		case 'NotFoundError':
			return { message: i18n.t(`${ERR}.deviceNotFound`), reportToSentry: false }
		case 'NotReadableError':
			return { message: i18n.t(`${ERR}.deviceBusy`), reportToSentry: false }
		default:
			return { message: i18n.t(`${ERR}.generic`), reportToSentry: true }
	}
}

export function mapDisplayMediaFailure(e: unknown): { message: string; reportToSentry: boolean } {
	const name =
		e && typeof e === 'object' && 'name' in e && typeof (e as DOMException).name === 'string'
			? (e as DOMException).name
			: ''
	switch (name) {
		case 'NotAllowedError':
		case 'PermissionDenied':
			return {
				message: i18n.t('pages.projectDetail.audioRecorder.meetingAudioShareCancelled'),
				reportToSentry: false,
			}
		case 'NotSupportedError':
			return {
				message: i18n.t('pages.projectDetail.audioRecorder.meetingAudioUnsupportedBrowser'),
				reportToSentry: false,
			}
		default:
			return { message: i18n.t(`${ERR}.meetingAudioGeneric`), reportToSentry: true }
	}
}

export async function requestMeetingAudioStream(): Promise<{
	stream: MediaStream
	hasAudioTrack: boolean
}> {
	const stream = await navigator.mediaDevices.getDisplayMedia({
		video: true,
		audio: true,
		systemAudio: 'include',
		suppressLocalAudioPlayback: false,
	} as DisplayMediaStreamOptionsWithSystemAudio)

	for (const track of stream.getVideoTracks()) {
		track.stop()
	}

	return {
		stream,
		hasAudioTrack: stream.getAudioTracks().length > 0,
	}
}

export function mixAudioStreams(
	audioContext: AudioContext,
	micStream: MediaStream,
	meetingStream: MediaStream | null
): MediaStream {
	const destination = audioContext.createMediaStreamDestination()
	const micGain = audioContext.createGain()
	micGain.gain.value = 0.5
	const micSource = audioContext.createMediaStreamSource(micStream)
	micSource.connect(micGain)
	micGain.connect(destination)

	if (meetingStream !== null) {
		const meetingAudioTracks = meetingStream.getAudioTracks()
		if (meetingAudioTracks.length > 0) {
			const meetingGain = audioContext.createGain()
			meetingGain.gain.value = 0.5
			const meetingSource = audioContext.createMediaStreamSource(meetingStream)
			meetingSource.connect(meetingGain)
			meetingGain.connect(destination)
		}
	}

	return destination.stream
}

const RECORDER_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'] as const

export function pickRecorderMime(): { mimeType: string; extension: 'webm' | 'm4a' } {
	for (const mime of RECORDER_MIME_CANDIDATES) {
		if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
			const base = mime.split(';')[0] ?? mime
			return { mimeType: mime, extension: base === 'audio/mp4' ? 'm4a' : 'webm' }
		}
	}
	return { mimeType: '', extension: 'webm' }
}

export function mediaRecorderSupportsPause(): boolean {
	return typeof MediaRecorder !== 'undefined' && 'pause' in MediaRecorder.prototype
}
